import * as d3 from 'd3';
import { prepareMACD } from '../prepIndicator/prepareMACD';
import theme from '../../theme';

const macdStyles = {
    line: theme.indicators.macd.line,
    signal: theme.indicators.macd.signal,
    histogram: {
        positive: theme.indicators.macd.histogramPos,
        negative: theme.indicators.macd.histogramNeg,
    },
};

export const drawMACD = (svg, data, xScale, yScale, values) => {
    try {
        //console.log("Rendering MACD with seamless dark styling...");

        // Prepare MACD data
        const macdData = prepareMACD(data, values);
        //console.log("Prepared MACD data:", macdData);

        if (!macdData || !macdData.macd || !macdData.signal || !macdData.histogram) {
            console.warn('Incomplete MACD data:', macdData);
            return;
        }

        // Filter out invalid data points
        const validMACDData = macdData.macd.filter(d => !isNaN(d.y) && d.y !== null && isFinite(d.y));
        const validSignalData = macdData.signal.filter(d => !isNaN(d.y) && d.y !== null && isFinite(d.y));
        const validHistogramData = macdData.histogram.filter(d => !isNaN(d.y) && d.y !== null && isFinite(d.y));

        if (validMACDData.length === 0 && validSignalData.length === 0 && validHistogramData.length === 0) {
            console.warn('No valid MACD data points found');
            return;
        }

        //console.log("MACD rendering completed with seamless dark styling.");

        // Add subtle zero line for reference
        drawZeroLine(svg, xScale, yScale);

        // Draw MACD histogram first (background layer) with seamless styling
        if (validHistogramData.length > 0) {
            drawMACDHistogram(svg, validHistogramData, xScale, yScale);
        }

        // Draw MACD lines on top with seamless styling
        if (validMACDData.length > 0) {
            drawMACDLine(svg, validMACDData, xScale, yScale, macdStyles.line, 'macd-line');
        }
        
        if (validSignalData.length > 0) {
            drawMACDLine(svg, validSignalData, xScale, yScale, macdStyles.signal, 'macd-signal');
        }

    } catch (error) {
        console.warn('Error rendering MACD:', error);
    }
};

const drawZeroLine = (svg, xScale, yScale) => {
    svg.append('line')
        .attr('class', 'macd-zero-line')
        .attr('x1', 0)
        .attr('x2', xScale.range()[1])
        .attr('y1', yScale(0))
        .attr('y2', yScale(0))
        .attr('stroke', theme.grid.color)
        .attr('stroke-width', 1)
        .attr('stroke-dasharray', '2,2')
        .attr('opacity', 0.4); // Subtle for seamless integration
};

const drawMACDLine = (svg, lineData, xScale, yScale, color, className) => {
    if (!lineData || lineData.length === 0) {
        console.warn(`No data to draw MACD line: ${className}`);
        return;
    }

    //console.log(`Rendering seamless MACD line: ${className}, Data Points: ${lineData.length}`);

    // Create line generator with better curve handling
    const lineGenerator = d3.line()
        .x(d => xScale(d.x))
        .y(d => yScale(d.y))
        .defined(d => !isNaN(d.y) && d.y !== null && isFinite(d.y))
        .curve(d3.curveMonotoneX);

    // Add subtle glow effect for better visibility on dark background
    svg.append("path")
        .datum(lineData)
        .attr("class", `${className}-glow`)
        .attr("fill", "none")
        .attr("stroke", color)
        .attr("stroke-width", 4)
        .attr("stroke-opacity", 0.2) // Very subtle glow
        .attr("stroke-linecap", "round")
        .attr("stroke-linejoin", "round")
        .attr("d", lineGenerator);

    // Main line with seamless styling
    svg.append("path")
        .datum(lineData)
        .attr("class", className)
        .attr("fill", "none")
        .attr("stroke", color)
        .attr("stroke-width", 2)
        .attr("stroke-linecap", "round")
        .attr("stroke-linejoin", "round")
        .attr("opacity", 0.9) // Slightly transparent for seamless blending
        .attr("d", lineGenerator);
};

const drawMACDHistogram = (svg, histogramData, xScale, yScale) => {
    if (!histogramData || histogramData.length === 0) {
        console.warn("No histogram data available to draw.");
        return;
    }

    // Calculate optimal bar width for seamless appearance
    const timeRange = xScale.domain()[1] - xScale.domain()[0];
    const barWidth = Math.max(1, (xScale.range()[1] - xScale.range()[0]) / histogramData.length * 0.85);

    //console.log("Rendering seamless histogram bars...");
    
    // Create histogram bars with seamless dark theme styling
    const bars = svg.selectAll('.macd-histogram')
        .data(histogramData)
        .enter()
        .append("rect")
        .attr("class", "macd-histogram")
        .attr("x", d => xScale(d.x) - barWidth / 2)
        .attr("y", d => d.y >= 0 ? yScale(d.y) : yScale(0))
        .attr("width", barWidth)
        .attr("height", d => Math.abs(yScale(d.y) - yScale(0)))
        .attr("fill", d => d.y >= 0 ? macdStyles.histogram.positive : macdStyles.histogram.negative)
        .attr("opacity", 0.6) // Semi-transparent for seamless blending
        .attr("stroke", "none") // Remove stroke for seamless look
        .attr("rx", 0.5); // Very subtle rounding for modern look

    // Add subtle animation on render for better UX
    bars.attr("height", 0)
        .transition()
        .duration(800)
        .ease(d3.easeCubicOut)
        .attr("height", d => Math.abs(yScale(d.y) - yScale(0)))
        .attr("y", d => d.y >= 0 ? yScale(d.y) : yScale(0));

    //console.log("Seamless histogram bars rendered");
};